import { z } from 'zod'
import { booleanoDeQuery } from './comum'

/**
 * Estatísticas (5.5).
 *
 * Todos os indicadores filtráveis por intervalo de datas, de forma agregada ou
 * por usuário individual.
 */

export const filtroEstatisticasSchema = z.object({
  de: z.coerce.date(),
  ate: z.coerce.date(),
  /** Em branco = a equipe toda. */
  usuarioId: z.string().uuid().optional(),
  /**
   * Recorte por setor. Conversa entra pelo setor do atendimento, o que inclui
   * a que está na fila sem dono; venda e negociação entram por quem atende no
   * setor, porque venda não tem setor, tem vendedor.
   */
  setorId: z.string().uuid().optional(),
  incluirSemTarefa: booleanoDeQuery(true),
})
export type FiltroEstatisticas = z.infer<typeof filtroEstatisticasSchema>

export interface PontoDoDia {
  dia: string
  valor: number
}

export interface FatiaPorEtapa {
  etapaId: string
  etapaNome: string
  etapaCor: string
  funilNome: string
  quantidade: number
  valor: number
}

export interface FatiaPorMotivo {
  motivoId: string
  motivoNome: string
  quantidade: number
  valor: number
}

export interface FatiaPorVendedor {
  /** `null` na linha "Sem vendedor": venda ou perda de card que estava sem dono. */
  usuarioId: string | null
  nome: string
  ganhos: number
  valorGanho: number
  perdas: number
  chamados: number
  /** Leads de anúncio das conversas que esta pessoa atende, chegados no período. */
  leadsDeAnuncio: number
  /** Desses leads, os qualificados no período. */
  leadsQualificados: number
}

export interface MetaAcompanhada {
  id: string
  tipo: TipoMeta
  usuarioId: string | null
  usuarioNome: string | null
  periodoInicio: string
  periodoFim: string
  valorAlvo: number
  valorRealizado: number
  percentual: number
}

// ─────────────── o que a página ganhou depois da primeira versão ───────────────

/** Uma fatia por setor: com venda, suporte e financeiro juntos, a média mistura times diferentes. */
export interface FatiaPorSetor {
  setorId: string | null
  setorNome: string
  chamados: number
  /** Em minutos. Null quando ninguém respondeu nada no período. */
  tempoMedioPrimeiraRespostaMinutos: number | null
  estourados: number
}

/** Uma casa do mapa de calor: dia da semana (0=domingo) × hora do dia. */
export interface PontoDePico {
  diaDaSemana: number
  hora: number
  quantidade: number
}

/**
 * "Quando o cliente escreve" com período próprio, escolhido na régua do
 * cartão, independente do filtro da página.
 */
export const filtroDemandaSchema = z.object({
  de: z.coerce.date(),
  ate: z.coerce.date(),
})

export interface DemandaDto {
  pontos: PontoDePico[]
}

/** Mensagens recebidas numa semana, de segunda a domingo, no fuso da empresa. */
export interface SemanaDeDemanda {
  /** Segunda-feira que abre a semana, AAAA-MM-DD. */
  inicio: string
  quantidade: number
}

/** O desenho da régua: toda semana desde a primeira mensagem recebida. */
export interface LinhaDoTempoDemandaDto {
  semanas: SemanaDeDemanda[]
}

/**
 * Os mesmos números no período imediatamente anterior, de mesma duração.
 *
 * Sem isto todo indicador é absoluto, e "11 chamados" não diz se foi um bom
 * período. A comparação é o que transforma o número em informação.
 */
export interface ResumoComparavel {
  leadsDeAnuncio: number
  leadsQualificados: number
  chamadosTotal: number
  clientesNovos: number
  leadsGanhos: number
  valorGanho: number
  ticketMedio: number | null
  taxaConversao: number | null
  tempoMedioPrimeiraRespostaMinutos: number | null
}

export interface EstatisticasDto {
  periodo: { de: string; ate: string }

  /** Atendimento */
  chamadosPorDia: PontoDoDia[]
  chamadosTotal: number
  chamadosAbertosAgora: number
  /** Em minutos, da abertura ao fechamento. */
  tempoMedioConversaMinutos: number | null
  /** Em minutos, da mensagem do cliente até a primeira resposta (4.5). */
  tempoMedioPrimeiraRespostaMinutos: number | null
  /**
   * Prospecção ativa (5.5): conversas que o vendedor iniciou no botão "+" do
   * bate-papo com um número que ainda não estava na base.
   */
  chamadosProspeccao: number
  /**
   * Clientes que falaram com a empresa pela primeira vez no período.
   *
   * Conta pessoa, não conversa: quem escreveu cinco vezes na semana em que
   * apareceu entra uma vez só. Sai da data da primeira mensagem de entrada do
   * contato, e não do selo de 72h da tela — o selo é o aviso para o atendente,
   * e some; o número aqui vale para qualquer período, inclusive anos atrás.
   */
  clientesNovos: number
  clientesNovosPorDia: PontoDoDia[]

  /** Funil */
  leadsAbertos: number
  leadsGanhos: number
  leadsPerdidos: number
  leadsSemTarefa: number
  valorGanho: number
  valorPerdido: number
  ganhosPorEtapa: FatiaPorEtapa[]
  perdasPorEtapa: FatiaPorEtapa[]
  perdasPorMotivo: FatiaPorMotivo[]
  vendasPorDia: PontoDoDia[]

  /** Equipe */
  porVendedor: FatiaPorVendedor[]

  /** Ticket médio do período: valor ganho por negociação ganha. */
  ticketMedio: number | null
  /** Ganhas sobre ganhas mais perdidas, de 0 a 1. Null sem nenhum fechamento. */
  taxaConversao: number | null

  /**
   * Conversas abertas no período em que a primeira resposta ao cliente passou
   * do prazo configurado, ou ainda não saiu e o prazo já venceu. A média
   * esconde isto.
   */
  cronometroEstourados: number
  /** O prazo usado na conta acima, em horas (Configuração, Sistema). */
  prazoPrimeiraRespostaHoras: number
  porSetor: FatiaPorSetor[]
  picoPorHora: PontoDePico[]
  /** Contatos da carteira sem nenhuma conversa há mais de 60 dias. */
  clientesParados: number

  /** Leads de anúncio. Ver `ResumoDeAnuncios`. */
  anuncios: ResumoDeAnuncios

  /** Período anterior, para a comparação de cada indicador. */
  anterior: ResumoComparavel

  /** Metas (5.5) */
  metas: MetaAcompanhada[]
}

// ─────────────────────────── anúncios ───────────────────────────

/**
 * Leads de anúncio no período. **Cada número conta pela data em que a coisa
 * aconteceu**, a mesma regra das metas (as duas telas leem as mesmas contas no
 * servidor): chegada pela data em que o lead chegou, qualificação pela data em
 * que foi qualificado.
 *
 * A tela de Anúncios faz outra conta de propósito — dos leads que um anúncio
 * trouxe, quantos prestaram —, porque a pergunta de lá é sobre o anúncio, e a
 * daqui é sobre o trabalho do período.
 */
export interface ResumoDeAnuncios {
  leads: number
  qualificados: number
  desqualificados: number
  /** Retrato de agora, sem período: leads que ninguém avaliou ainda. */
  semAvaliacaoAgora: number
  /** Qualificados sobre qualificados mais desqualificados. Null sem nenhuma avaliação no período. */
  taxaQualificacao: number | null
}

// ─────────────────────────── metas ───────────────────────────

export const TIPOS_META = ['faturamento', 'leads_qualificados'] as const
export type TipoMeta = (typeof TIPOS_META)[number]

export const ROTULO_TIPO_META: Record<TipoMeta, string> = {
  faturamento: 'Valor vendido',
  leads_qualificados: 'Leads de anúncio qualificados',
}

/**
 * Como cada meta é contada, dito na tela de criar a meta.
 *
 * Meta cuja regra ninguém sabe vira discussão no fim do mês. As regras são as
 * mesmas dos números das Estatísticas.
 */
export const DESCRICAO_TIPO_META: Record<TipoMeta, string> = {
  faturamento: 'Soma das vendas ganhas no período.',
  leads_qualificados:
    'Leads de anúncio qualificados no período. Conta para quem atende a conversa.',
}

/** Só a meta de valor é em reais; as outras são quantidade. */
export function metaEmReais(tipo: TipoMeta): boolean {
  return tipo === 'faturamento'
}

export const criarMetaSchema = z
  .object({
    /** Em branco = meta da equipe. */
    usuarioId: z.string().uuid().optional().nullable(),
    /** Sem tipo é valor vendido: é o que toda meta era antes de existir tipo. */
    tipo: z.enum(TIPOS_META).default('faturamento'),
    periodoInicio: z.coerce.date(),
    periodoFim: z.coerce.date(),
    valorAlvo: z.number().min(0).max(999_999_999),
  })
  .refine((m) => m.periodoInicio <= m.periodoFim, {
    message: 'o início precisa ser antes do fim',
    path: ['periodoFim'],
  })
  /*
    Meta de quantidade é número inteiro e ao menos um.

    "2,5 leads" não existe, e meta zero de leads é meta batida no primeiro
    dia: o medidor mostraria 100% sem ninguém ter feito nada.
  */
  .refine((m) => metaEmReais(m.tipo) || (Number.isInteger(m.valorAlvo) && m.valorAlvo >= 1), {
    message: 'meta de quantidade precisa ser um número inteiro, a partir de 1',
    path: ['valorAlvo'],
  })

export interface MetaDto {
  id: string
  tipo: TipoMeta
  usuarioId: string | null
  usuarioNome: string | null
  periodoInicio: string
  periodoFim: string
  valorAlvo: number
  valorRealizado: number
  percentual: number
}
